import { ReportQuartiles } from '../../../types/Quartiles'
import { QuartileBand } from '../../../types/Statistic'
import { Quartile } from '../index'
import dayjs from 'dayjs'
import { ScoreCollection } from '../../entities/Result/scores'
import { $t } from '../../../translations/index'
import { ResultSet } from '../../../types/ResultSet'
import checkTopPercentile from './checkTopPercentile'

export function getQuartileNumberTranslation (score: number, quartiles: QuartileBand[]): string | undefined {
  const breaksLowerBand: boolean = score < quartiles[0].lower
  if (breaksLowerBand) {
    return $t('report.overallEffectiveness.legend.1stQuartile') as string
  }
  const breaksUpperBand: boolean = score > quartiles[quartiles.length - 1].upper
  if (breaksUpperBand) {
    return $t('report.overallEffectiveness.legend.4thQuartile') as string
  }

  for (let quartile: number = 0; quartile < quartiles.length; quartile++) {
    const {
      lower,
      upper
    } = quartiles[quartile]

    const withinLowerBand: boolean = score >= lower
    const withinUpperBand: boolean = quartile === quartiles.length - 1
      ? score <= upper
      : score < upper

    if (withinLowerBand && withinUpperBand) {
      switch (quartile) {
        case 0:
          return $t('report.overallEffectiveness.legend.1stQuartile') as string
        case 1:
          return $t('report.overallEffectiveness.legend.2ndQuartile') as string
        case 2:
          return $t('report.overallEffectiveness.legend.3rdQuartile') as string
        case 3:
          return $t('report.overallEffectiveness.legend.4thQuartile') as string
      }
    }
  }
}

export function getQuartilesDescription (resultSet: ResultSet, reportQuartiles: ReportQuartiles, comparison?: boolean): string {
  const lowestBand: QuartileBand | undefined = reportQuartiles.quartiles.quartileBands.find(
    (quartile: QuartileBand): boolean => quartile.quartile === Quartile.Q1
  )
  const highestBand: QuartileBand | undefined = reportQuartiles.quartiles.quartileBands.find(
    (quartile: QuartileBand): boolean => quartile.quartile === Quartile.Q4
  )

  if (!lowestBand || !highestBand) {
    throw new Error('Failed to get Quartile description, Quartile bands are not defined')
  }
  const topPercentile: string | undefined = checkTopPercentile(reportQuartiles.quartiles.overallScore, lowestBand.lower, highestBand.upper)
  const topPercentileText: string | undefined = topPercentile
    ? $t(`report.overallEffectiveness.topPercentileBands.${topPercentile}`) as string
    : undefined

  const scoreQuartileText: string | undefined = getQuartileNumberTranslation(reportQuartiles.quartiles.overallScore, reportQuartiles.quartiles.quartileBands)
  if (!scoreQuartileText) {
    throw new Error('Failed to get Quartile description, Quartile number is not defined')
  }
  const quartileDate: string = comparison && resultSet.comparisonCreatedAt ? resultSet.comparisonCreatedAt : resultSet.createdAt
  const date: string = dayjs(quartileDate).format('MM/YYYY')

  const universeText: string = reportQuartiles.subTitle ? `${reportQuartiles.title} - ${reportQuartiles.subTitle}` : reportQuartiles.title
  switch (reportQuartiles.collection) {
    case ScoreCollection.SystemAverage:
      if (comparison) {
        return topPercentile
          ? $t('report.overallEffectiveness.system.comparison.topQuartileDescription', [topPercentileText, date]) as string
          : $t('report.overallEffectiveness.system.comparison.quartileDescription', [scoreQuartileText, date]) as string
      }
      return topPercentile
        ? $t('report.overallEffectiveness.system.topQuartileDescription', [topPercentileText, date]) as string
        : $t('report.overallEffectiveness.system.quartileDescription', [scoreQuartileText, date]) as string
    case ScoreCollection.UniverseAverage:
      if (comparison) {
        return topPercentile
          ? $t('report.overallEffectiveness.universe.comparison.topQuartileDescription', [topPercentileText, universeText, date]) as string
          : $t('report.overallEffectiveness.universe.comparison.quartileDescription', [scoreQuartileText, universeText, date]) as string
      }
      return topPercentile
        ? $t('report.overallEffectiveness.universe.topQuartileDescription', [topPercentileText, universeText, date]) as string
        : $t('report.overallEffectiveness.universe.quartileDescription', [scoreQuartileText, universeText, date]) as string
  }

  return $t('report.overallEffectiveness.quartileDescription') as string
}
